import {Playlist} from "./Playlist.ts";
import useStore from "../store.ts";
import {setHideFlag} from "../utils/ElementUtils.ts";

export class PlaylistManager {
    private readonly playlists: Map<string, any>;
    /* see docs/EXTRACTED_COMMENTS.md#c724 */
    private readonly missing: Set<string>;

    constructor() {
        this.playlists = new Map();
        this.missing = new Set();
    }

    getPlaylists() {
        return this.playlists;
    }


    addPlaylist(playlistName: string) {
        const playlist = new Playlist(playlistName);
        this.playlists.set(playlistName, playlist);
        return playlist;
    }

    getPlaylist(name: string): Playlist | undefined {
        return this.playlists.get(name);
    }

    listPlaylistNames(): string[] {
        return [...this.playlists.keys()];
    }

    /* see docs/EXTRACTED_COMMENTS.md#c093 */
    syncToServer(): void {
        const store = useStore();
        if (!store.socketManager) return;
        for (const name of this.listPlaylistNames()) {
            store.socketManager.commandQueue.send("request_playlist", {playlist_id: name});
        }
    }

    /**
     * RECONFIGURE_PLAYLIST: the server dropped `names` (`null`: every playlist) from its cache;
     * re-request the ones this display holds. The replies fire each playlist's callbacks.
     */
    reloadPlaylists(names: string[] | null | undefined): void {
        const store = useStore();
        if (!store.socketManager) return;
        const held = this.listPlaylistNames();
        const targets = names == null ? held : held.filter((name) => names.includes(name));
        for (const name of targets) {
            store.socketManager.commandQueue.send("request_playlist", {playlist_id: name});
        }
    }

    /** The server answered "Playlist was not found." for this name. */
    markMissing(playlistName: string): void {
        if (!playlistName) return;
        this.missing.add(playlistName);
        this.applyHideFlags();
    }

    /** The server delivered playlist content, so the name resolves after all. */
    markPresent(playlistName: string): void {
        // Only the transition out of "missing" can unhide anything.
        if (!playlistName || !this.missing.delete(playlistName)) return;
        this.applyHideFlags();
    }

    isMissing(playlistName: string): boolean {
        return this.missing.has(playlistName);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c725 */
    applyHideFlags(): void {
        const store = useStore();
        for (const id of Object.keys(store.components)) {
            const item = store.components[id];
            if (!item?.playlist_props) continue;
            const src = item.published?.src ?? "";
            setHideFlag(id, "playlist", !!src && this.missing.has(src), "playlistMissing", true);
        }
    }

    clearPlaylists() {
        this.playlists.clear();
        this.missing.clear();
    }
}
